import React, { useEffect, useImperativeHandle, useState } from 'react'
import { Check, Loader2, Plus, ThumbsDown, ThumbsUp, Trash2 } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import { TagInput } from '@/features/company-profile'
import type { CommunicationGuidelines, ExampleEmail, ObjectionResponse } from '../types'

interface Props {
  initial: CommunicationGuidelines | null
  saving: boolean
  onSave: (input: CommunicationGuidelines) => Promise<unknown>
  onDirtyChange?: (dirty: boolean) => void
}

export type CommunicationGuidelinesFormHandle = {
  save: () => Promise<void>
  discard: () => void
}

function normalizeGuidelines(initial: CommunicationGuidelines | null): CommunicationGuidelines {
  return {
    ...initial,
    doList: initial?.doList ?? [],
    dontList: initial?.dontList ?? [],
    exampleEmails: initial?.exampleEmails ?? [],
    objectionResponses: initial?.objectionResponses ?? [],
    cadence: {
      maxTouches: initial?.cadence?.maxTouches,
      minDaysBetweenTouches: initial?.cadence?.minDaysBetweenTouches,
      stopConditions: initial?.cadence?.stopConditions ?? [],
    },
  }
}

export const CommunicationGuidelinesForm = React.forwardRef<CommunicationGuidelinesFormHandle, Props>(
  ({ initial, saving, onSave, onDirtyChange }, ref) => {
    const { currentTheme: t } = useTheme()
    const [saved, setSaved] = useState<CommunicationGuidelines>(() => normalizeGuidelines(initial))
    const [guidelines, setGuidelines] = useState<CommunicationGuidelines>(() => normalizeGuidelines(initial))
    const [savedMessage, setSavedMessage] = useState<string | null>(null)
    const [formError, setFormError] = useState<string | null>(null)

    const isDirty = JSON.stringify(guidelines) !== JSON.stringify(saved)

    useEffect(() => {
      onDirtyChange?.(isDirty)
    }, [isDirty, onDirtyChange])

    useEffect(() => () => onDirtyChange?.(false), [onDirtyChange])

    const inputStyle = { backgroundColor: t.inputBackground, color: t.text, borderColor: t.border }
    const labelClass = 'block text-sm font-semibold mb-2'
    const inputClass = 'w-full rounded-xl border px-4 py-2.5 text-sm outline-none transition'
    const addButtonClass = 'flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-semibold transition'

    const updateField = <K extends keyof CommunicationGuidelines>(key: K, value: CommunicationGuidelines[K]) => {
      setGuidelines((prev) => ({ ...prev, [key]: value }))
      setSavedMessage(null)
    }

    const updateExampleEmails = (rows: ExampleEmail[]) => updateField('exampleEmails', rows)
    const updateObjectionResponses = (rows: ObjectionResponse[]) => updateField('objectionResponses', rows)

    // Same tolerance as IdentityProfileForm — drop only fully-blank rows before
    // saving, so a half-filled row still surfaces a clear backend validation error
    // rather than silently vanishing.
    const buildSavePayload = (): CommunicationGuidelines => ({
      ...guidelines,
      exampleEmails: (guidelines.exampleEmails ?? []).filter((r) => r.label.trim() || r.subject.trim() || r.body.trim()),
      objectionResponses: (guidelines.objectionResponses ?? []).filter((r) => r.objection.trim() || r.response.trim()),
    })

    const handleSave = async () => {
      setFormError(null)
      setSavedMessage(null)
      try {
        await onSave(buildSavePayload())
        setSaved(guidelines)
        setSavedMessage('Saved.')
      } catch (err) {
        setFormError(err instanceof Error ? err.message : 'Failed to save. Please try again.')
      }
    }

    useImperativeHandle(ref, () => ({
      save: handleSave,
      discard: () => {
        setGuidelines(saved)
        setFormError(null)
        setSavedMessage(null)
      },
    }))

    return (
      <div>

        {/* Voice & Tone */}
        <section className="pb-6 mb-6 border-b" style={{ borderColor: t.border }}>
          <h3 className="text-base font-semibold mb-4" style={{ color: t.text }}>Voice &amp; Tone</h3>
          <div className="space-y-4">
            <div>
              <label className={labelClass} style={{ color: t.text }}>
                Voice description
                <span className="ml-2 font-normal" style={{ color: t.mutedText }}>{(guidelines.voiceDescription ?? '').length}/500</span>
              </label>
              <textarea
                value={guidelines.voiceDescription ?? ''}
                onChange={(e) => updateField('voiceDescription', e.target.value.slice(0, 500))}
                placeholder="e.g. Professional but warm. Avoid jargon and hard-sell language."
                rows={2}
                className={inputClass}
                style={inputStyle}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={labelClass} style={{ color: t.text }}>Do</label>
                <TagInput
                  value={guidelines.doList ?? []}
                  onChange={(doList) => updateField('doList', doList)}
                  placeholder="e.g. Lead with the prospect's problem"
                />
              </div>
              <div>
                <label className={labelClass} style={{ color: t.text }}>Don't</label>
                <TagInput
                  value={guidelines.dontList ?? []}
                  onChange={(dontList) => updateField('dontList', dontList)}
                  placeholder="e.g. Don't mention pricing before a call"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Example Emails */}
        <section className="pb-6 mb-6 border-b" style={{ borderColor: t.border }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold" style={{ color: t.text }}>Example Emails</h3>
            <button
              type="button"
              onClick={() => updateExampleEmails([...(guidelines.exampleEmails ?? []), { label: '', subject: '', body: '', verdict: 'good' }])}
              className={addButtonClass}
              style={{ backgroundColor: t.hoverBackground, color: t.text }}
            >
              <Plus size={13} /> Add
            </button>
          </div>
          <div className="space-y-3">
            {(guidelines.exampleEmails ?? []).length === 0 && (
              <p className="text-sm" style={{ color: t.mutedText }}>Add a good or bad example email for Zeph to learn from.</p>
            )}
            {(guidelines.exampleEmails ?? []).map((row, idx) => {
              const update = (patch: Partial<ExampleEmail>) => {
                const next = [...(guidelines.exampleEmails ?? [])]
                next[idx] = { ...row, ...patch }
                updateExampleEmails(next)
              }
              return (
                <div key={idx} className="rounded-xl border p-3 space-y-2" style={{ borderColor: t.border }}>
                  <div className="flex items-center gap-2">
                    <input
                      value={row.label}
                      onChange={(e) => update({ label: e.target.value })}
                      placeholder="Label, e.g. Cold intro — good example"
                      className={`${inputClass} flex-1`}
                      style={inputStyle}
                    />
                    <button
                      type="button"
                      onClick={() => update({ verdict: 'good' })}
                      className="flex items-center gap-1 rounded-lg px-2.5 py-2 text-xs font-semibold transition shrink-0"
                      style={{
                        backgroundColor: row.verdict === 'good' ? `${t.successText}18` : t.hoverBackground,
                        color: row.verdict === 'good' ? t.successText : t.mutedText,
                      }}
                    >
                      <ThumbsUp size={13} /> Good
                    </button>
                    <button
                      type="button"
                      onClick={() => update({ verdict: 'bad' })}
                      className="flex items-center gap-1 rounded-lg px-2.5 py-2 text-xs font-semibold transition shrink-0"
                      style={{
                        backgroundColor: row.verdict === 'bad' ? `${t.errorText}18` : t.hoverBackground,
                        color: row.verdict === 'bad' ? t.errorText : t.mutedText,
                      }}
                    >
                      <ThumbsDown size={13} /> Bad
                    </button>
                    <button
                      type="button"
                      onClick={() => updateExampleEmails((guidelines.exampleEmails ?? []).filter((_, i) => i !== idx))}
                      className="rounded-lg p-2.5 transition hover:opacity-70 shrink-0"
                      style={{ color: t.errorText }}
                      aria-label="Remove"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                  <input
                    value={row.subject}
                    onChange={(e) => update({ subject: e.target.value })}
                    placeholder="Subject"
                    className={inputClass}
                    style={inputStyle}
                  />
                  <textarea
                    value={row.body}
                    onChange={(e) => update({ body: e.target.value })}
                    placeholder="Body"
                    rows={3}
                    className={inputClass}
                    style={inputStyle}
                  />
                  <input
                    value={row.note ?? ''}
                    onChange={(e) => update({ note: e.target.value })}
                    placeholder="Note — why this is good or bad (optional)"
                    className={inputClass}
                    style={inputStyle}
                  />
                </div>
              )
            })}
          </div>
        </section>

        {/* Objection Playbook */}
        <section className="pb-6 mb-6 border-b" style={{ borderColor: t.border }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold" style={{ color: t.text }}>Objection Playbook</h3>
            <button
              type="button"
              onClick={() => updateObjectionResponses([...(guidelines.objectionResponses ?? []), { objection: '', response: '' }])}
              className={addButtonClass}
              style={{ backgroundColor: t.hoverBackground, color: t.text }}
            >
              <Plus size={13} /> Add
            </button>
          </div>
          <div className="space-y-3">
            {(guidelines.objectionResponses ?? []).length === 0 && (
              <p className="text-sm" style={{ color: t.mutedText }}>Add common objections and Zeph's approved response.</p>
            )}
            {(guidelines.objectionResponses ?? []).map((row, idx) => (
              <div key={idx} className="grid gap-2 sm:grid-cols-[1fr_2fr_auto] items-start rounded-xl border p-3" style={{ borderColor: t.border }}>
                <input
                  value={row.objection}
                  onChange={(e) => {
                    const next = [...(guidelines.objectionResponses ?? [])]
                    next[idx] = { ...row, objection: e.target.value }
                    updateObjectionResponses(next)
                  }}
                  placeholder="Objection"
                  className={inputClass}
                  style={inputStyle}
                />
                <input
                  value={row.response}
                  onChange={(e) => {
                    const next = [...(guidelines.objectionResponses ?? [])]
                    next[idx] = { ...row, response: e.target.value }
                    updateObjectionResponses(next)
                  }}
                  placeholder="Approved response"
                  className={inputClass}
                  style={inputStyle}
                />
                <button
                  type="button"
                  onClick={() => updateObjectionResponses((guidelines.objectionResponses ?? []).filter((_, i) => i !== idx))}
                  className="rounded-lg p-2.5 transition hover:opacity-70"
                  style={{ color: t.errorText }}
                  aria-label="Remove"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* Follow-up Cadence */}
        <section>
          <h3 className="text-base font-semibold mb-4" style={{ color: t.text }}>Follow-up Cadence</h3>
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={labelClass} style={{ color: t.text }}>Max touches</label>
                <input
                  type="number"
                  min={0}
                  max={50}
                  value={guidelines.cadence?.maxTouches ?? ''}
                  onChange={(e) => updateField('cadence', {
                    ...guidelines.cadence,
                    maxTouches: e.target.value === '' ? undefined : Number(e.target.value),
                  })}
                  placeholder="e.g. 4"
                  className={inputClass}
                  style={inputStyle}
                />
              </div>
              <div>
                <label className={labelClass} style={{ color: t.text }}>Minimum days between touches</label>
                <input
                  type="number"
                  min={0}
                  max={365}
                  value={guidelines.cadence?.minDaysBetweenTouches ?? ''}
                  onChange={(e) => updateField('cadence', {
                    ...guidelines.cadence,
                    minDaysBetweenTouches: e.target.value === '' ? undefined : Number(e.target.value),
                  })}
                  placeholder="e.g. 3"
                  className={inputClass}
                  style={inputStyle}
                />
              </div>
            </div>
            <div>
              <label className={labelClass} style={{ color: t.text }}>Stop conditions</label>
              <TagInput
                value={guidelines.cadence?.stopConditions ?? []}
                onChange={(stopConditions) => updateField('cadence', { ...guidelines.cadence, stopConditions })}
                placeholder="e.g. Prospect replies"
              />
            </div>
          </div>
        </section>

        {/* Footer */}
        <div
          className="mt-5 pt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
          style={{ borderTop: `1px solid ${t.border}` }}
        >
          <div className="text-sm" style={{ color: t.secondrytext }}>
            {formError && <span style={{ color: t.errorText }}>{formError}</span>}
            {savedMessage && !formError && <span style={{ color: t.successText }}>{savedMessage}</span>}
          </div>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving}
            className="flex items-center justify-center gap-2 h-10 px-5 rounded-full text-[13.5px] font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 shrink-0"
            style={{ background: t.accentGradient, boxShadow: '0 1px 6px rgba(79,70,229,0.25)' }}
          >
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    )
  },
)

export default React.memo(CommunicationGuidelinesForm)
